import React, { useState } from 'react';
import {
  BrainCircuit,
  Activity,
  AlertTriangle,
  Building2,
  RefreshCw,
  Sparkles,
  ArrowUpRight,
  ShieldAlert,
  Zap,
  CheckCircle2,
  KeyRound,
  Radio
} from 'lucide-react';
import { DepartmentMetric, OperationalAlert, Patient, EarlyDeteriorationSignal, ClinicalBusiness } from '../types';
import { generateExecutiveBriefing } from '../services/geminiService';
import { NativeMarkdownViewer } from './NativeMarkdownViewer';
import { EarlyDeteriorationSentryCard } from './EarlyDeteriorationSentryCard';

interface CommandCenterProps {
  departmentMetrics: DepartmentMetric[];
  alerts: OperationalAlert[];
  patients: Patient[];
  deteriorationSignals?: EarlyDeteriorationSignal[];
  onSelectPatient: (patient: Patient) => void;
  onResolveAlert: (id: string) => void;
  onNavigateToDocs: () => void;
  onPlaceSentryOrder?: (signalId: string) => void;
  currentBusiness?: ClinicalBusiness;
  onOpenAccessModal?: () => void;
  onNavigateToAmbient?: () => void;
}

export const CommandCenter: React.FC<CommandCenterProps> = ({
  departmentMetrics,
  alerts,
  patients,
  deteriorationSignals = [],
  onSelectPatient,
  onResolveAlert,
  onNavigateToDocs,
  onPlaceSentryOrder,
  currentBusiness,
  onOpenAccessModal,
  onNavigateToAmbient
}) => {
  const [briefing, setBriefing] = useState<string>(
    `• Overall inpatient load is stable at 88.4% capacity. Intensive Care requires active decompression triage.\n• Critical Early Deterioration Sentry triggered for Harold Greenfield (Suite 112): gait slowing (-15%) & doubled bathroom visits signal subclinical UTI/delirium.\n• AI recommends accelerating discharge summaries for 2 stable patients in Step-Down.`
  );
  const [isGeneratingBrief, setIsGeneratingBrief] = useState<boolean>(false);

  const totalBeds = currentBusiness?.authorizedMaxBeds || departmentMetrics.reduce((acc, curr) => acc + curr.totalBeds, 0);
  const rawOccupiedBeds = departmentMetrics.reduce((acc, curr) => acc + curr.occupiedBeds, 0);
  const occupiedBeds = Math.min(rawOccupiedBeds, totalBeds);
  const occupancyPct = (occupiedBeds / totalBeds) * 100;
  const criticalCasesTotal = departmentMetrics.reduce((acc, curr) => acc + curr.criticalCases, 0);

  const handleRefreshBriefing = async () => {
    setIsGeneratingBrief(true);
    const alertText = alerts.map((a) => `${a.title}: ${a.description}`).join(' | ');
    const result = await generateExecutiveBriefing(alertText, occupancyPct);
    setBriefing(result);
    setIsGeneratingBrief(false);
  };

  return (
    <div className="space-y-6">
      {/* Workspace License Status Banner */}
      {currentBusiness && (
        <div className={`p-4 rounded-2xl border flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-lg ${
          currentBusiness.licenseTier === 'ENTERPRISE_UNIFIED'
            ? 'bg-gradient-to-r from-teal-950/40 via-slate-900 to-slate-950 border-teal-500/40 text-teal-300'
            : currentBusiness.licenseTier === 'COMMERCIAL_PRO'
            ? 'bg-gradient-to-r from-cyan-950/40 via-slate-900 to-slate-950 border-cyan-500/40 text-cyan-300'
            : 'bg-gradient-to-r from-amber-950/40 via-slate-900 to-slate-950 border-amber-500/40 text-amber-300'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-xl border ${
              currentBusiness.licenseTier === 'ENTERPRISE_UNIFIED'
                ? 'bg-teal-500/20 border-teal-500/40 text-teal-400'
                : currentBusiness.licenseTier === 'COMMERCIAL_PRO'
                ? 'bg-cyan-500/20 border-cyan-500/40 text-cyan-400'
                : 'bg-amber-500/20 border-amber-500/40 text-amber-400'
            }`}>
              <Building2 className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-extrabold text-white text-sm tracking-wide">{currentBusiness.name}</span>
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded font-bold uppercase ${
                  currentBusiness.licenseTier === 'ENTERPRISE_UNIFIED'
                    ? 'bg-teal-500/20 text-teal-300 border border-teal-500/40'
                    : currentBusiness.licenseTier === 'COMMERCIAL_PRO'
                    ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40'
                    : 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                }`}>
                  {currentBusiness.licenseTier.replace('_', ' ')}
                </span>
                {currentBusiness.licenseTier === 'BETA_TRIAL' && (
                  <span className="text-[10px] font-mono text-amber-400 bg-amber-950/60 px-2 py-0.5 rounded border border-amber-500/40">
                    {currentBusiness.trialDaysRemaining}d Sandbox
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-400 mt-0.5">
                {currentBusiness.facilityType} • Licensed Bed Capacity: <strong className="text-white font-mono">{currentBusiness.authorizedMaxBeds} Beds</strong> • NPI: {currentBusiness.activeNpi}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {onOpenAccessModal && (
              <button
                onClick={onOpenAccessModal}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-200 border border-slate-700 text-xs font-semibold transition group"
              >
                <KeyRound className="h-3.5 w-3.5 text-teal-400 group-hover:rotate-45 transition-transform" />
                <span>Manage License & Tiers</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Quick Launch Banner: Aqara mmWave Radar Ambient Safety Hub */}
      {onNavigateToAmbient && (
        <div className="p-4 rounded-2xl bg-gradient-to-r from-teal-950/60 via-slate-900 to-slate-900 border-2 border-teal-500/40 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xl shadow-teal-950/30">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-teal-500/20 border border-teal-500/40 text-teal-300">
              <Radio className="h-6 w-6 text-teal-400 animate-pulse" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-extrabold text-white text-sm">
                  Ambient Safety &amp; Telemetry Hub (Aqara mmWave Radars)
                </h4>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-teal-500/20 text-teal-300 border border-teal-500/40 font-bold uppercase">
                  Rooms 101–103 Active
                </span>
              </div>
              <p className="text-[11px] text-slate-300 mt-0.5">
                Real-time 60GHz mmWave fall detection, posture monitoring, and hardware pairing bridge.
              </p>
            </div>
          </div>

          <button
            onClick={onNavigateToAmbient}
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-slate-950 text-xs font-bold transition flex items-center justify-center gap-1.5 shadow-md shadow-teal-500/20 active:scale-95 shrink-0"
          >
            <span>Open Ambient Safety Hub</span>
            <ArrowUpRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Top Banner: Executive HUD Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-slate-900 to-slate-950 p-5 rounded-2xl border border-slate-800 relative overflow-hidden shadow-lg">
          <div className="absolute top-0 right-0 p-4 opacity-10">
            <Building2 className="h-20 w-20 text-teal-400" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono uppercase text-slate-400">Total Bed Census</span>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-teal-500/10 text-teal-400 border border-teal-500/20">
              {occupiedBeds}/{totalBeds} BEDS
            </span>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-white">{occupancyPct.toFixed(1)}%</span>
            <span className="text-xs text-amber-400 font-medium">Near Capacity</span>
          </div>
          <div className="w-full bg-slate-800 h-1.5 rounded-full mt-3 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                occupancyPct > 90 ? 'bg-rose-500' : 'bg-teal-400'
              }`}
              style={{ width: `${Math.min(occupancyPct, 100)}%` }}
            />
          </div>
        </div>

        <div className="bg-gradient-to-br from-slate-900 to-slate-950 p-5 rounded-2xl border border-slate-800 relative overflow-hidden shadow-lg">
          <div className="absolute top-0 right-0 p-4 opacity-10">
            <Activity className="h-20 w-20 text-rose-500" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono uppercase text-slate-400">Critical Care Count</span>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-rose-500/10 text-rose-400 border border-rose-500/20">
              PRIORITY 1
            </span>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-rose-400">{criticalCasesTotal}</span>
            <span className="text-xs text-slate-400">Patients in ICU/CCU</span>
          </div>
          <p className="text-[11px] text-slate-400 mt-2 flex items-center gap-1">
            <Zap className="h-3 w-3 text-amber-400" /> 2 cases awaiting surgical slot
          </p>
        </div>

        <div className="bg-gradient-to-br from-slate-900 to-slate-950 p-5 rounded-2xl border border-slate-800 relative overflow-hidden shadow-lg">
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono uppercase text-slate-400">Active ER Triage</span>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
              DOOR-TO-DOCTOR
            </span>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-white">18.4</span>
            <span className="text-xs text-slate-400">min average</span>
          </div>
          <p className="text-[11px] text-emerald-400 mt-2 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" /> 14% faster than state benchmark
          </p>
        </div>

        <div className="bg-gradient-to-br from-slate-900 to-slate-950 p-5 rounded-2xl border border-slate-800 relative overflow-hidden shadow-lg">
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono uppercase text-slate-400">AI Early Sentry</span>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-rose-500/20 text-rose-300 border border-rose-500/40">
              PROACTIVE
            </span>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-rose-400">{deteriorationSignals.length}</span>
            <span className="text-xs text-slate-400">Signals Detected</span>
          </div>
          <p className="text-[11px] text-rose-300 mt-2 flex items-center gap-1">
            <ShieldAlert className="h-3 w-3 text-rose-400" /> Subtle triad degradation flags
          </p>
        </div>
      </div>

      {/* AI Early Deterioration Sentry Section */}
      {deteriorationSignals.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-bold text-white flex items-center gap-2">
                <ShieldAlert className="h-5 w-5 text-rose-400 animate-pulse" />
                <span>AI Early Deterioration Sentry Feeds</span>
                <span className="bg-rose-500/20 text-rose-300 text-[10px] px-2 py-0.5 rounded font-mono font-bold">
                  GEMINI 2.5 FLASH PROACTIVE TRIAGE
                </span>
              </h2>
              <p className="text-xs text-slate-400">
                Detects pre-symptomatic triad deterioration (walking speed slowing, bathroom frequency spikes, resting pulse increases) to intercept falls.
              </p>
            </div>
            <span className="text-xs font-mono text-slate-400">
              {deteriorationSignals.filter((s) => !s.orderPlaced).length} Unaddressed Interventions
            </span>
          </div>

          <div className="space-y-4">
            {deteriorationSignals.map((sig) => (
              <EarlyDeteriorationSentryCard
                key={sig.id}
                signal={sig}
                onPlaceOrder={onPlaceSentryOrder ? onPlaceSentryOrder : () => {}}
              />
            ))}
          </div>
        </div>
      )}

      {/* AI Executive Briefing Card */}
      <div className="bg-slate-900/80 border border-teal-500/30 rounded-2xl p-6 relative shadow-xl shadow-teal-950/20">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-slate-800 gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-teal-500/10 border border-teal-500/30 text-teal-400">
              <BrainCircuit className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white flex items-center gap-2">
                Executive Situational Intelligence
                <span className="bg-teal-500/20 text-teal-300 text-[10px] px-2 py-0.5 rounded font-mono font-semibold">
                  GEMINI 2.5 FLASH POWERED
                </span>
              </h2>
              <p className="text-xs text-slate-400">
                Autonomous hospital operations synthesis, capacity prediction, and clinical risk mitigation.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleRefreshBriefing}
              disabled={isGeneratingBrief}
              className="flex items-center gap-1.5 text-xs bg-slate-800 hover:bg-slate-700 text-teal-300 border border-slate-700 px-3 py-2 rounded-xl transition disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isGeneratingBrief ? 'animate-spin' : ''}`} />
              <span>{isGeneratingBrief ? 'Analyzing Hospital Feeds...' : 'Refresh AI Assessment'}</span>
            </button>
            <button
              onClick={onNavigateToDocs}
              className="flex items-center gap-1.5 text-xs bg-teal-600 hover:bg-teal-500 text-slate-950 font-bold px-3 py-2 rounded-xl transition"
            >
              <span>Draft EHR SOAP Note</span>
              <ArrowUpRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        <div className="mt-4 p-4 rounded-xl bg-slate-950/60 border border-slate-800/80">
          <NativeMarkdownViewer content={briefing} />
        </div>
      </div>

      {/* Split Grid: Live Department Readiness & Critical Incident Feeds */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Department Readiness List */}
        <div className="lg:col-span-2 bg-slate-900/60 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-bold text-white">Department Flow & Bed Availability</h3>
              <p className="text-xs text-slate-400">Real-time nurse ratios and wait times across core wings</p>
            </div>
            <span className="text-[11px] font-mono text-slate-400">5 Divisions Monitored</span>
          </div>

          <div className="space-y-3">
            {departmentMetrics.map((dept) => {
              const capRatio = dept.occupiedBeds / dept.totalBeds;
              const isOverloaded = capRatio >= 0.9;

              return (
                <div
                  key={dept.department}
                  className="bg-slate-950/50 border border-slate-800/80 rounded-xl p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:border-slate-700 transition"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-slate-100">{dept.department}</span>
                      {isOverloaded && (
                        <span className="bg-rose-500/20 text-rose-300 border border-rose-500/40 text-[9px] px-1.5 py-0.2 rounded font-mono">
                          STRAIN
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-400 flex items-center gap-3">
                      <span>Staff Ratio: <strong className="text-slate-300 font-mono">{dept.nurseToPatientRatio}</strong></span>
                      <span>•</span>
                      <span>Avg Wait: <strong className="text-slate-300 font-mono">{dept.avgWaitMins}m</strong></span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-xs font-mono font-bold text-slate-200">
                        {dept.occupiedBeds} / {dept.totalBeds} Beds
                      </div>
                      <div className="text-[10px] text-slate-400">
                        {dept.criticalCases} Critical cases
                      </div>
                    </div>
                    <div className="w-24 bg-slate-800 h-2 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          capRatio >= 0.9 ? 'bg-rose-500' : capRatio >= 0.75 ? 'bg-amber-400' : 'bg-teal-400'
                        }`}
                        style={{ width: `${Math.min(capRatio * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Operational Incident Queue */}
        <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-400" />
                <span>Executive Alert Queue</span>
              </h3>
              <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-800 text-slate-300">
                {alerts.filter((a) => !a.resolved).length} Pending
              </span>
            </div>

            <div className="space-y-3">
              {alerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`p-3 rounded-xl border text-xs space-y-1.5 transition ${
                    alert.resolved
                      ? 'bg-slate-950/30 border-slate-800/40 opacity-50'
                      : alert.level === 'CRITICAL'
                      ? 'bg-rose-950/20 border-rose-800/50 text-rose-200'
                      : alert.level === 'WARNING'
                      ? 'bg-amber-950/20 border-amber-800/50 text-amber-200'
                      : 'bg-slate-950/40 border-slate-800 text-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-[9px] font-mono px-1.5 py-0.5 rounded font-bold ${
                        alert.level === 'CRITICAL'
                          ? 'bg-rose-500 text-slate-950'
                          : alert.level === 'WARNING'
                          ? 'bg-amber-400 text-slate-950'
                          : 'bg-slate-800 text-slate-300'
                      }`}
                    >
                      {alert.level}
                    </span>
                    <span className="text-[10px] text-slate-400 font-mono">{alert.timestamp}</span>
                  </div>

                  <h4 className="font-semibold text-slate-100">{alert.title}</h4>
                  <p className="text-[11px] text-slate-300 leading-snug">{alert.description}</p>

                  <div className="pt-1 flex items-center justify-between">
                    <span className="text-[10px] text-slate-400 font-mono">{alert.department}</span>
                    {!alert.resolved && (
                      <button
                        onClick={() => onResolveAlert(alert.id)}
                        className="text-[10px] bg-slate-800 hover:bg-slate-700 text-teal-300 px-2 py-1 rounded transition border border-slate-700 font-semibold"
                      >
                        Acknowledge
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-slate-800 flex items-center justify-between text-xs text-slate-400">
            <span>Hospital Telemetry Protocol</span>
            <span className="text-teal-400 font-mono">v4.8-SYNC</span>
          </div>
        </div>
      </div>
    </div>
  );
};
